import React, {useEffect, useState} from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Tabs,
    Tab,
    Button,
    Divider,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {styled} from "@mui/system";
import {ApiDetails, KEY} from "../../../../Constants";
import MessagesComponent from "../Message/MessagesComponent";
import TenantReport from "../Report/TenantReport";
import CryptoJS from "crypto-js";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import {TransitionProps} from "@mui/material/transitions";
import Slide from "@mui/material/Slide";
import PropertyTenants from "../Property Tenants/PropertyTenantsNew";
import PaymentFormComponent from "../Form/Payment Details Form/FormComponent";
import UpdatePropertyDetails from "../Property Details/UpdatePropertyDetails";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';
import { getUserData } from '../../../../services/api/client';


const themeColor = "#2C5283";

const StyledCard = styled(Card)(({theme}) => ({
    marginBottom: theme.spacing(2),
    border: "1px solid #DDE6ED",
    borderRadius: theme.spacing(1),
    // Mobile-friendly card styling
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(1),
        borderRadius: theme.spacing(0.5),
    }
}));

// Mobile-friendly header container
const HeaderContainer = styled(Box)(({theme}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // Stack vertically on mobile
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
    }
}));

// Mobile-friendly button container
const ButtonContainer = styled(Box)(({theme}) => ({
    display: "flex",
    gap: theme.spacing(1),
    // Stack buttons on mobile
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        flexDirection: 'column',
        gap: theme.spacing(1),
    }
}));

// Responsive typography
const ResponsiveTypography = styled(Typography)(({theme}) => ({
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.1rem',
        marginBottom: theme.spacing(1),
    }
}));


const RightSide = (props) => {
    const {propertyDetails, tenants, setBillTenantsModal, billTenantsModal} = props;

    // Responsive hooks
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    let myMapDetailsDecrypted = "";

    try {
        myMapDetailsDecrypted = CryptoJS.AES.decrypt(localStorage.getItem("myMap"), KEY);
    } catch (e) {
        console.log(e);
    }

    const originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    // Helper function to get auth token
    const getAuthToken = () => {
        const userData = getUserData();
        return userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;
    };

    const [activeTab, setActiveTab] = React.useState(0);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const broadCastCredentials = () => {
        let values = {
            property_id: propertyDetails.property_id,
            landlord_id: JSON.parse(originalText)[0][1],
        };

        fetch(ApiDetails + "pegasus/visionary/messages/broadcast/credentials", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify(values),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Credentials resent successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else if (response.status === 500) {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong. Kindly try again later..",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Kindly try again later..",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    return (
        <>
            {/* Content wrapper - Grid item handled by parent */}
            <Box sx={{ width: '100%' }}>
                {/* Mobile-friendly Tabs Section */}
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    textColor="primary"
                    indicatorColor="primary"
                    variant={isMobile ? "scrollable" : "standard"}
                    scrollButtons={isMobile ? "auto" : false}
                    allowScrollButtonsMobile
                    sx={{
                        marginBottom: 2,
                        '& .MuiTab-root': {
                            minWidth: isMobile ? 120 : 'auto',
                            fontSize: isMobile ? '0.875rem' : '1rem',
                            padding: isMobile ? '6px 8px' : '6px 12px',
                        },
                        // Hide scrollbar on mobile for cleaner look
                        '& .MuiTabs-scroller': {
                            '&::-webkit-scrollbar': {
                                display: 'none',
                            },
                            scrollbarWidth: 'none',
                        }
                    }}
                >
                    <Tab label="Tenants"/>
                    <Tab label={isMobile ? "Property" : "Property Details"}/>
                    <Tab label={isMobile ? "Payment" : "Payment Details"}/>
                    <Tab label={isMobile ? "Broadcast" : "Broadcast Message"}/>
                </Tabs>

                {/* Tab Content */}
                {activeTab === 0 && (
                    <StyledCard>
                        <CardContent sx={{
                            padding: isMobile ? 2 : 3,
                            '&:last-child': {
                                paddingBottom: isMobile ? 2 : 3
                            }
                        }}>
                            <HeaderContainer>
                                <ResponsiveTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    color={themeColor}
                                    gutterBottom={isMobile}
                                >
                                    View Property Tenants
                                </ResponsiveTypography>

                                <ButtonContainer>
                                    <Button
                                        variant="outlined"
                                        size={isMobile ? "medium" : "medium"}
                                        fullWidth={isMobile}
                                        sx={{
                                            textTransform: "none",
                                            backgroundColor: "#FBF4F0",
                                            minHeight: isMobile ? 44 : 'auto', // Better touch target
                                            fontSize: isMobile ? '0.875rem' : '1rem',
                                        }}
                                        onClick={() => {
                                            setBillTenantsModal(true)
                                        }}
                                    >
                                        Bill Tenants
                                    </Button>
                                </ButtonContainer>
                            </HeaderContainer>

                            <Divider sx={{
                                marginY: isMobile ? "8px" : "10px",
                                backgroundColor: "#2C5283"
                            }}/>

                            <PropertyTenants
                                tenants={tenants}
                                originalText={originalText}
                                waterBill={propertyDetails.water_bill}
                                minimumWaterBill={propertyDetails.minimum_water_bill}
                                electricityBill={propertyDetails.electricity_bill}
                                minimumElectricityBill={propertyDetails.minimum_electricity_bill}
                                propertyDetails={propertyDetails}
                                viewModal={billTenantsModal}
                            />
                        </CardContent>
                    </StyledCard>
                )}

                {activeTab === 1 && (
                    <StyledCard>
                        <CardContent sx={{
                            padding: isMobile ? 2 : 3,
                            '&:last-child': {
                                paddingBottom: isMobile ? 2 : 3
                            }
                        }}>
                            <UpdatePropertyDetails
                                propertyDetails={propertyDetails}
                                originalDetails={originalText}
                            />
                        </CardContent>
                    </StyledCard>
                )}

                {activeTab === 2 && (
                    <StyledCard>
                        <CardContent sx={{
                            padding: isMobile ? 2 : 3,
                            '&:last-child': {
                                paddingBottom: isMobile ? 2 : 3
                            }
                        }}>
                            <ResponsiveTypography
                                variant="h6"
                                fontWeight="bold"
                                color={themeColor}
                                gutterBottom
                            >
                                Edit Payment Details
                            </ResponsiveTypography>

                            <Divider sx={{
                                marginY: isMobile ? "8px" : "10px",
                                backgroundColor: "#2C5283"
                            }}/>

                            <PaymentFormComponent
                                propertyDetails={propertyDetails}
                                originalDetails={originalText}
                            />
                        </CardContent>
                    </StyledCard>
                )}

                {activeTab === 3 && (
                    <StyledCard>
                        <CardContent sx={{
                            padding: isMobile ? 2 : 3,
                            '&:last-child': {
                                paddingBottom: isMobile ? 2 : 3
                            }
                        }}>
                            <HeaderContainer>
                                <ResponsiveTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    color={themeColor}
                                    gutterBottom={isMobile}
                                >
                                    Broadcast A Message
                                </ResponsiveTypography>

                                <ButtonContainer>
                                    <Button
                                        variant="outlined"
                                        size={isMobile ? "medium" : "medium"}
                                        fullWidth={isMobile}
                                        sx={{
                                            textTransform: "none",
                                            backgroundColor: "#FBF4F0",
                                            minHeight: isMobile ? 44 : 'auto', // Better touch target
                                            fontSize: isMobile ? '0.875rem' : '1rem',
                                        }}
                                        onClick={broadCastCredentials}
                                    >
                                        Resend Credentials
                                    </Button>
                                </ButtonContainer>
                            </HeaderContainer>

                            <Divider sx={{
                                marginY: isMobile ? "8px" : "10px",
                                backgroundColor: "#2C5283"
                            }}/>

                            <MessagesComponent
                                propertyDetails={propertyDetails}
                                originalText={originalText}
                            />
                        </CardContent>
                    </StyledCard>
                )}

                {activeTab === 4 && (
                    <StyledCard>
                        <CardContent sx={{
                            padding: isMobile ? 2 : 3,
                            '&:last-child': {
                                paddingBottom: isMobile ? 2 : 3
                            }
                        }}>
                            <ResponsiveTypography
                                variant="h6"
                                fontWeight="bold"
                                color={themeColor}
                                gutterBottom
                            >
                                Tenant Report Generator
                            </ResponsiveTypography>

                            <Divider sx={{
                                marginY: isMobile ? "8px" : "10px",
                                backgroundColor: "#2C5283"
                            }}/>

                            <TenantReport propertyDetails={propertyDetails}/>
                        </CardContent>
                    </StyledCard>
                )}
            </Box>
        </>
    );
};

export default RightSide;